import { useMemo, useCallback, useEffect } from "react";
import { Drawer, Form, Select } from "antd";
import Button from "@/common/atoms/Button";
import Label from "@/common/atoms/lable";
import { generatePriceOptions as generateAreaSizeOptions } from "@/utils/generatePriceOptions";
import { SelectField } from "../Select";
import { getSizeOptions } from "@/constants/miscConstants";
import StepperInput from "@/common/atoms/Input/steperInput";
import { setStateFilterCount } from "@/features/property/duck/property";
import { Divider } from "antd";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { countAppliedFilters } from "@/utils/index";
const areaSizeOptions = generateAreaSizeOptions(50, 0, 0, 100000, 50);

const PropertyDetailsDrawer = ({
  visible,
  setVisible,
  resetFilters,
  filterObject,
  onApplyFilter
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const selectClasses = `font-sans !border-1 !border-[#D7DCE0] text-[13px]
                 [&.ant-select>.ant-select-selector]:bg-[#F3F3F3]
                  [&.ant-select>.ant-select-selector>.ant-select-selection-wrap>.ant-select-selection-placeholder]:!text-[#32493B]
                  [&.ant-select>.ant-select-selector>.ant-select-selection-wrap>.ant-select-selection-item]:!text-[#32493B]
                   rounded-lg !h-[42px] [&.ant-select>.ant-select-selector]:hover:!border-[#D7DCE0] 
                   [&_.ant-select-selector]:!shadow-none [&_.ant-select-selector]:!outline-none [&_.ant-select-selector]:!border-[#D7DCE0] [&_.ant-select-arrow]:bg-white 
                    [&_.ant-select-arrow]:rounded-full 
                    [&_.ant-select-arrow]:text-[8px] 
                    [&_.ant-select-arrow]:py-[8px] 
                    [&_.ant-select-arrow]:px-[4px] 
                    [&_.ant-select-arrow]:!text-[#32493B] 
                    [&_.ant-select-arrow]:flex 
                    [&_.ant-select-arrow]:items-center 
                    [&_.ant-select-arrow]:!font-bold`;

  const filteredMaxOptions = useMemo(
    () =>
      filterObject?.minPropertySize
        ? areaSizeOptions.filter((size) => size > filterObject?.minPropertySize)
        : areaSizeOptions,
    [filterObject?.minPropertySize]
  );

  const initialValues = useMemo(
    () => ({
      minPropertySize: filterObject?.minPropertySize,
      maxPropertySize: filterObject?.maxPropertySize,
      minNoBathroom: filterObject?.minNoBathroom || 0,
      maxNoBathroom: filterObject?.maxNoBathroom || 0,
      minNoBedroom: filterObject?.minNoBedroom || 0,
      maxNoBedroom: filterObject?.maxNoBedroom || 0,
      minNoKitchen: filterObject?.minNoKitchen || 0,
      maxNoKitchen: filterObject?.maxNoKitchen || 0,
      propertyPricePrefix: filterObject?.propertyPricePrefix,
      propertySizePostfix: filterObject?.propertySizePostfix
    }),
    [filterObject]
  );

  const onFinish = useCallback(
    (values) => {
      const isFalse = Object.values(values).every(
        (value) => value === 0 || value === undefined
      );
      if (!isFalse) {
        onApplyFilter(values);
        setVisible(false);
      }
    },
    [(onApplyFilter, setVisible)]
  );

  const handleClose = () => setVisible(false);

  const handleResetFilters = () => {
    setVisible(false);
    resetFilters();
    form.resetFields();
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (formValues && Object.keys(formValues).length > 0) {
      const appliedFiltersCount = countAppliedFilters(formValues);
      dispatch(setStateFilterCount(appliedFiltersCount)); // dispatch updated filter count
    }
  }, [formValues, dispatch]);

  const onValuesChange = useCallback(
    (changedValues, allValues) => {
      countAppliedFilters(allValues);
    },
    [countAppliedFilters]
  );
  return (
    <Drawer
      title={
        <div className="font-manropeMedium text-[#000929] text-lg">
          {t("filter.moreDetails")}
        </div>
      }
      placement="right"
      onClose={handleClose}
      open={visible}
      width={440}
      closeIcon={null}
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="outline"
            className="w-full h-11 rounded-xl"
            onClick={handleResetFilters}>
            {t("filter.resetFilter")}
          </Button>
          <Form.Item noStyle>
            <Button
              htmlType="submit"
              form="propertyDetailsForm"
              className="w-full h-11 rounded-xl">
              {t("common.apply")}
            </Button>
          </Form.Item>
        </div>
      }
      className="py-2 ltr:pl-8 ltr:pr-24 rtl:pr-8 rtl:pl-24 [&.ant-drawer-content>.ant-drawer-body]:overflow-hidden [&.ant-drawer-content>.ant-drawer-footer]:px-0 [&.ant-drawer-content>.ant-drawer-footer]:border-t-0 [&.ant-drawer-content>.ant-drawer-footer]:mb-4 [&.ant-drawer-content>.ant-drawer-header]:border-b-0 ltr:[&.ant-drawer-content>.ant-drawer-header]:!pl-0 rtl:[&.ant-drawer-content>.ant-drawer-header]:!pr-0">
      <button
        onClick={handleClose}
        className="absolute ltr:right-6 rtl:left-6 top-6 text-base font-bold text-[#1D1B20] hover:text-gray-900">
        ✕
      </button>
      <Form
        form={form}
        id="propertyDetailsForm"
        name="propertyDetails"
        onFinish={onFinish}
        layout="vertical"
        className="mt-4 h-full"
        initialValues={initialValues}
        onValuesChange={onValuesChange}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Form.Item
              className="text-[#000929] font-manropeMedium text-sm"
              name="propertySizePostfix"
              label={t("filter.propertyArea")}>
              <Select
                className={`w-full ${selectClasses}`}
                placeholder={t("filter.selectArea")}
                options={getSizeOptions(t)}
              />
            </Form.Item>
          </div>
        </div>
        <Divider className="border-[#F0EFFB] m-0 mb-6" />

        <Label
          title={t("common.propertySize")}
          className="!text-[#000929] !font-manropeMedium !mb-2 block"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectField
            name="minPropertySize"
            placeholder={
              filterObject?.minPropertySize || `${t("filter.selectMin")}`
            }
            options={areaSizeOptions}
            selectClasses={selectClasses}
          />
          <SelectField
            name="maxPropertySize"
            placeholder={
              filterObject?.maxPropertySize || `${t("filter.selectMax")}`
            }
            options={filteredMaxOptions}
            selectClasses={selectClasses}
          />
        </div>
        <Divider className="border-[#F0EFFB] m-0" />
        <h3 className="font-manropeMedium text-[#000929] py-4 mt-2">
          {t("filter.features")}
        </h3>

        <div className="grid grid-cols-[150px_auto] gap-6 items-baseline">
          <Label
            title={t("filter.bathroom")}
            className="!font-sans !text-[#000929]"
          />
          <StepperInput name="maxNoBathroom" min={0} max={99} />
        </div>

        <div className="grid grid-cols-[150px_auto] gap-6 items-baseline">
          <Label
            title={t("filter.bedroom")}
            className="!font-sans !text-[#000929]"
          />
          <StepperInput name="maxNoBedroom" min={0} max={99} />
        </div>

        <div className="grid grid-cols-[150px_auto] gap-6 items-baseline">
          <Label
            title={t("filter.kitchen")}
            className="!font-sans !text-[#000929]"
          />
          <StepperInput name="maxNoKitchen" min={0} max={99} />
        </div>
        <Divider className="border-[#F0EFFB] m-0 mb-4" />
      </Form>
    </Drawer>
  );
};

export default PropertyDetailsDrawer;
